/**
 * Frontend mirror of backend/src/utils/money.js (owner decision 2026-10-07,
 * docs/decisions/2026-10-07-ceny-jednostkowe-dwa-miejsca.md: every unit price
 * is entered, stored and displayed with 2 decimal places, half-up rounded; a
 * positive price that would round to 0,00 becomes 0,01; amounts/totals stay
 * 2 decimal places and an entered/invoiced line net is never recomputed).
 *
 * Keep this file's rounding/line-value math identical to the backend
 * implementation. Only formatting helpers (which reuse numberFormatting.ts)
 * are frontend-specific.
 */
import { formatLocalizedNumber, localeForLanguage, THOUSANDS_GROUPING } from './numberFormatting';

export const UNIT_PRICE_DECIMALS = 2;
export const MONEY_DECIMALS = 2;
export const UNIT_PRICE_MIN = 0.01;
const QUANTITY_DECIMALS = 3;

const DECIMAL_EPSILON = 1e-9;

/**
 * Mirror of backend money.js. Half-up rounding to a fixed number of decimals on the DECIMAL value — the one
 * rounding of money AND quantities (owner 2026-10-07: „do 3 miejsc po przecinku
 * … globalnie, wszędzie”; commercialQuantity.ts roundCommercialQuantity uses it).
 * A scaled figure within a few ulps below the half-step counts as the half-step,
 * so binary noise still rounds like the decimal it stands for: 70 × 0.05175 = 3.6224999… → 3.623,
 * 1.005 → 1.01. Negative half-steps round towards +∞ (Math.round), as before.
 */
export function roundDecimalHalfUp(value: unknown, decimals: number): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return Number.NaN;
  const factor = 10 ** decimals;
  const scaled = numeric * factor;
  if (!Number.isFinite(scaled)) return numeric;
  const floor = Math.floor(scaled);
  // A few ulps of binary noise below the half-step still count as the half-step.
  const noise = Math.abs(scaled) * 2 * Number.EPSILON;
  const rounded = (scaled - floor >= 0.5 - noise ? floor + 1 : floor) / factor;
  return rounded === 0 ? 0 : rounded;
}

/** 2dp half-up; a positive price that rounds to 0 becomes UNIT_PRICE_MIN (mirror of money.js). */
export function roundUnitPrice(value: unknown): number {
  const rounded = roundDecimalHalfUp(value, UNIT_PRICE_DECIMALS);
  return rounded === 0 && Number(value) > 0 ? UNIT_PRICE_MIN : rounded;
}

export function roundMoney(value: unknown): number {
  return roundDecimalHalfUp(value, MONEY_DECIMALS);
}

export function hasUnitPricePrecision(value: unknown): boolean {
  const numeric = Number(value);
  const rounded = roundUnitPrice(value);
  return Number.isFinite(numeric) && Number.isFinite(rounded) && Math.abs(numeric - rounded) < DECIMAL_EPSILON;
}

function toScaledInteger(rounded: number, decimals: number): bigint | null {
  if (!Number.isFinite(rounded) || rounded < 0) return null;
  return BigInt(rounded.toFixed(decimals).replace('.', ''));
}

/** Exact 3dp quantity × 2dp unit price (roundUnitPrice), rounded half-up to monetary cents. */
export function calculateLineValue(pricingQuantity: unknown, unitPrice: unknown): number {
  const quantityScaled = toScaledInteger(roundDecimalHalfUp(pricingQuantity, QUANTITY_DECIMALS), QUANTITY_DECIMALS);
  const priceScaled = toScaledInteger(roundUnitPrice(unitPrice), UNIT_PRICE_DECIMALS);
  if (quantityScaled === null || priceScaled === null) return Number.NaN;
  // value × 10^(QUANTITY_DECIMALS + UNIT_PRICE_DECIMALS) → cents (10^MONEY_DECIMALS)
  const divisor = BigInt(10 ** (QUANTITY_DECIMALS + UNIT_PRICE_DECIMALS - MONEY_DECIMALS));
  const cents = (quantityScaled * priceScaled + divisor / BigInt(2)) / divisor;
  return Number(cents) / 100;
}

/**
 * Localized unit price display: exactly 2 decimals after roundUnitPrice, so a
 * stored legacy 4dp price shows as it would be saved (0.333 -> "0,33", 5 -> "5,00").
 */
export function formatUnitPrice(value: number | null | undefined, language?: string): string {
  const numericValue = roundUnitPrice(value);
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
  return safeValue.toLocaleString(localeForLanguage(language), {
    minimumFractionDigits: MONEY_DECIMALS,
    maximumFractionDigits: UNIT_PRICE_DECIMALS,
    ...THOUSANDS_GROUPING,
  } as Intl.NumberFormatOptions);
}

/** Localized money/amount display: always 2 decimal places. */
export function formatMoney(value: number | null | undefined, language?: string): string {
  return formatLocalizedNumber(value, MONEY_DECIMALS, language);
}

/** Every unit price field: 2 decimals (owner 2026-10-07 „ceny do 2”). */
export const UNIT_PRICE_INPUT_PROPS = { precision: UNIT_PRICE_DECIMALS, step: 0.01, min: 0 } as const;

/**
 * Kept for its call sites (ZK/ZD price fields): since 2026-10-07 every price,
 * including a stored legacy 4dp one, is shown and saved at 2 decimals, so the
 * value no longer changes the props. The backend rounds whatever arrives.
 */
export const unitPriceInputProps = (_value?: number | null) => UNIT_PRICE_INPUT_PROPS;
